import React from 'react';
import { Box, useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { ArcButton } from '../../../../common/components/ArcButton';
import { useGenerateLocalAylaMatterPairingCodeMutation } from './localAylaMatterDevicesApi';
import { MatterPairingCodeModal } from './MatterPairingCodeModal';

type Props = {
  matterDeviceId?: string; // matterDeviceId can be undefined if it's not available locally
  isCommissionedLocally: boolean;
  isParentLoaded: boolean;
};

export const ShareMatterPairCodeButton = ({ matterDeviceId, isCommissionedLocally, isParentLoaded }: Props) => {
  const toast = useToast({ isClosable: true });
  const { t } = useTranslation();
  const [generateLocalMatterPairingCode, { data: pairCode, isLoading: isPairCodeLoading }] =
    useGenerateLocalAylaMatterPairingCodeMutation();
  const { isOpen: isPairCodeModalOpen, onOpen: onPairCodeModalOpen, onClose: onPairCodeModalClose } = useDisclosure();

  const handleUnavailableFeatureClick = () => {
    if (!isCommissionedLocally) {
      toast({
        title: t('Devices.feature only available if commissioned locally'),
        status: 'info',
        id: 'not-commissioned-locally-toast',
      });
    }
  };
  /* istanbul ignore next -- @preserve */
  const handleGeneratePairCode = async () => {
    try {
      if (!matterDeviceId) throw Error('Matter device ID not found');
      await generateLocalMatterPairingCode({
        matterDeviceId,
      }).unwrap();
      onPairCodeModalOpen();
    } catch (error) {
      toast({
        title: t('Devices.error creating pairing code'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  };
  return (
    <>
      <Box width="100%" textAlign="center" position="relative" data-testid="share-pairing-code-box">
        <Box
          onClick={handleUnavailableFeatureClick}
          position="absolute"
          w="100%"
          h="100%"
          zIndex={999}
          hidden={isCommissionedLocally}
        />
        <ArcButton
          arcColor="#3DCD57"
          width="90%"
          data-testid="share-pairing-code-button"
          isLoading={isPairCodeLoading || !isParentLoaded}
          isDisabled={!isCommissionedLocally}
          onClick={handleGeneratePairCode}
        >
          {t('Devices.share pair code button')}
        </ArcButton>
      </Box>
      {pairCode && (
        <MatterPairingCodeModal isOpen={isPairCodeModalOpen} onClose={onPairCodeModalClose} pairingCode={pairCode} />
      )}
    </>
  );
};
